import { useState, useEffect } from "react";
import { BellRing, MessageCircle, MessagesSquare, Inbox as InboxIcon, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { useResource, fmt, timeAgo, HubRow, SubHeader, EmptyState } from "./Shared";
import { ConfirmButton, runAction, BroadcastDialog } from "../AdminActions";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const HUB = [
  { key: "tasks", icon: BellRing, title: "Pending Tasks & Alerts", subtitle: "Everything waiting on you right now" },
  { key: "chats", icon: MessageCircle, title: "Support Chats", subtitle: "Customers and creators messaging you directly" },
  { key: "conversations", icon: MessagesSquare, title: "Order Conversations", subtitle: "Customer ↔ creator chats on live orders" },
  { key: "inbox", icon: InboxIcon, title: "Support Inbox", subtitle: "All support threads in one place" },
  { key: "announce", icon: Megaphone, title: "Send Announcement", subtitle: "Broadcast a message to users" },
];

function Tasks({ onBack }) {
  const { data, loading } = useResource("today");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const items = [
    ["Creator approvals waiting", data.pending_creator_approvals],
    ["Documents to review", data.pending_documents],
    ["Listings to review", data.pending_listings],
    ["Customer IDs to verify", data.pending_id_verifications],
    ["Open complaints", data.open_complaints],
    ["Support chats waiting", data.open_support_chats],
    ["Active orders", data.active_orders],
  ];
  return (
    <div data-testid="tasks-screen">
      <SubHeader title="Pending Tasks & Alerts" onBack={onBack} />
      <div className="space-y-2.5">
        {items.map(([label, n]) => (
          <div key={label} className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-4">
            <span className="text-sm font-semibold text-charcoal">{label}</span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${n > 0 ? "bg-terracotta text-white" : "bg-charcoal/5 text-charcoal/50"}`}>{n ?? 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatThread({ threadId, onBack, onDone }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const load = () => api.get(`/app-admin/detail/support-chat/${threadId}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load thread"));
  useEffect(() => { load(); }, [threadId]);
  if (!data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const messages = data.messages || data.thread?.messages || [];
  const who = data.user_name || data.thread?.user_name || "Conversation";
  return (
    <div data-testid="chat-thread">
      <SubHeader title={who} subtitle="Support conversation" onBack={onBack} />
      <div className="space-y-2">
        {messages.length === 0 ? <EmptyState icon={MessageCircle} text="No messages yet." /> :
          messages.map((m, i) => (
            <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${(m.from === "admin" || m.sender === "admin") ? "ml-auto bg-charcoal text-cream" : "bg-white text-charcoal border border-black/5"}`}>
              <p className="text-[11px] font-bold opacity-60">{m.name || m.from} · {fmt(m.at || m.created_at)}</p>
              <p className="mt-0.5">{m.text}</p>
            </div>
          ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Reply as Dishly…" data-testid="chat-reply-input" className="flex-1" />
        <ConfirmButton label="Send" confirmLabel="Send now?" variant="dark" testId="chat-send-btn"
          onConfirm={async () => {
            if (!text.trim()) return;
            await runAction("support_reply", threadId, { text: text.trim() });
            setText("");
            toast.success("Reply sent");
            load();
            onDone();
          }} />
      </div>
    </div>
  );
}

function SupportChats({ onBack }) {
  const { data, loading, reload } = useResource("support-chats");
  const [threadId, setThreadId] = useState(null);
  if (threadId) return <ChatThread threadId={threadId} onBack={() => setThreadId(null)} onDone={reload} />;
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="support-chats-screen">
      <SubHeader title="Support Chats" onBack={onBack} />
      <div className="space-y-2.5">
        {data.threads.length === 0 ? (
          <EmptyState icon={MessageCircle} text="No support chats yet. When a consumer or creator messages you, it lands here." />
        ) : data.threads.map((t) => (
          <button key={t.thread_id} onClick={() => setThreadId(t.thread_id)} data-testid={`chat-row-${t.thread_id}`}
            className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left hover:shadow-md">
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold text-charcoal">{t.user_name} <span className="text-xs font-semibold capitalize text-charcoal/45">({t.user_role})</span></p>
              <p className="mt-0.5 truncate text-[13px] text-charcoal/55">{t.last?.text || ""}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-xs text-charcoal/45">{timeAgo(t.updated_at)}</p>
              {t.unread_admin > 0 && <span className="mt-1 inline-block rounded-full bg-terracotta px-2 py-0.5 text-[11px] font-bold text-white">{t.unread_admin} new</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ConversationDetail({ orderId, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/app-admin/detail/conversation/${orderId}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load conversation"));
  }, [orderId]);
  if (!data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const messages = data.messages || [];
  return (
    <div data-testid="conversation-detail">
      <SubHeader title={`${data.customer_name || "Customer"} ↔ ${data.vendor_name || "Creator"}`} subtitle="Order conversation (read only)" onBack={onBack} />
      <div className="space-y-2">
        {messages.length === 0 ? <EmptyState icon={MessagesSquare} text="No messages." /> :
          messages.map((m, i) => (
            <div key={i} className="rounded-2xl border border-black/5 bg-white px-4 py-2.5 text-sm">
              <p className="text-[11px] font-bold text-charcoal/45">{m.name || m.from} · {fmt(m.at || m.created_at)}</p>
              <p className="mt-0.5 text-charcoal">{m.text}</p>
            </div>
          ))}
      </div>
    </div>
  );
}

function Conversations({ onBack }) {
  const { data, loading } = useResource("conversations");
  const [orderId, setOrderId] = useState(null);
  if (orderId) return <ConversationDetail orderId={orderId} onBack={() => setOrderId(null)} />;
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="conversations-screen">
      <SubHeader title="Order Conversations" onBack={onBack} />
      <div className="space-y-2.5">
        {data.conversations.length === 0 ? <EmptyState icon={MessagesSquare} text="No order conversations yet." /> :
          data.conversations.map((c) => (
            <button key={c.order_id} onClick={() => setOrderId(c.order_id)} data-testid={`conversation-row-${c.order_id}`}
              className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left hover:shadow-md">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-bold text-charcoal">{c.customer_name} ↔ {c.vendor_name}</p>
                <p className="mt-0.5 text-[13px] capitalize text-charcoal/55">{c.status} · {c.message_count} messages</p>
              </div>
              <span className="shrink-0 text-xs text-charcoal/45">{timeAgo(c.last_msg_at)}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

export default function InboxTab({ screen, goScreen }) {
  const [inner, setInner] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const active = screen || inner;
  const back = () => (screen ? goScreen(null) : setInner(null));
  const open = screen ? goScreen : setInner;

  if (active === "tasks") return <Tasks onBack={back} />;
  if (active === "chats" || active === "inbox") return <SupportChats onBack={back} />;
  if (active === "conversations") return <Conversations onBack={back} />;

  return (
    <div data-testid="inbox-tab" key={reloadKey}>
      <h2 className="font-display text-2xl font-bold text-charcoal">Inbox</h2>
      <p className="mb-5 mt-0.5 text-sm text-charcoal/55">Messages, alerts and announcements</p>
      <div className="space-y-2.5">
        {HUB.map((h) => (
          <HubRow key={h.key} icon={h.icon} title={h.title} subtitle={h.subtitle} testId={`inbox-hub-${h.key}`}
            onClick={() => h.key === "announce" ? setBroadcastOpen(true) : open(h.key)} />
        ))}
      </div>
      <BroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} onSent={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
